import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, GitBranch, Gauge } from "lucide-react";
import AnimatedHeadline from "@/components/AnimatedHeadline";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { SectionLabel } from "@/components/Bits";
import { DIFFERENTIATORS, PROCESS, SERVICES, STATS, IMAGES, COMPANY } from "@/lib/site";

const EASE = [0.16, 1, 0.3, 1];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] overflow-hidden bg-ink text-paper">
      <motion.div style={{ y }} className="absolute inset-0 -z-0">
        <img
          src={IMAGES.heroArch}
          alt="Modern commercial architecture representing structured systems"
          className="h-[118%] w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/60 to-ink" />
      </motion.div>

      <div className="relative mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-between px-6 pt-28 pb-10 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-3"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-clay" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-invtext">
            {COMPANY.category}
          </span>
        </motion.div>

        <div className="max-w-5xl py-16">
          <AnimatedHeadline
            className="font-display text-[13vw] font-medium leading-[0.95] tracking-tight sm:text-6xl lg:text-[5.5rem]"
            lines={["A predictable commercial", "insurance pipeline —", "engineered, not improvised."]}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
            className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-12"
          >
            <p className="max-w-xl leading-relaxed text-invtext md:col-span-7 md:text-lg">
              CCA becomes an extension of your business development — building a disciplined,
              accountable pipeline through proven systems, standardized processes, and quality
              assurance. Without the cost of an internal prospecting department.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row md:col-span-5 md:justify-end md:self-end">
              <Link
                to="/how-it-works"
                data-testid="hero-how-btn"
                className="group inline-flex items-center justify-center gap-2 border border-paper bg-paper px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.12em] text-ink transition-colors hover:bg-transparent hover:text-paper"
              >
                See How It Works
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div style={{ y: overlayY }} className="grid grid-cols-2 gap-px border-t border-inkline pt-8 md:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.9 + i * 0.1 }}
            >
              <p className="font-display text-3xl font-medium tracking-tight text-paper md:text-4xl">
                {s.value}
              </p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.12em] text-invtext">
                {s.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function CategoryStatement() {
  const items = [
    { icon: GitBranch, t: "Not lead generation", d: "We build owned, repeatable pipeline systems — not one-off lists." },
    { icon: ShieldCheck, t: "Not appointment setting", d: "Quality-assured engagement that protects and elevates your brand." },
    { icon: Gauge, t: "Not telemarketing", d: "Disciplined, professional business development your producers can forecast against." },
  ];
  return (
    <section className="border-b border-line bg-paper py-24 md:py-32" data-testid="category-statement">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Reveal>
              <SectionLabel num="00">The Category</SectionLabel>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display text-3xl font-medium leading-[1.08] tracking-tight text-ink sm:text-4xl lg:text-5xl">
                Commercial Insurance Pipeline Development.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-md leading-relaxed text-subtext">
                A discipline of its own. We define it, and we lead it — exclusively for independent
                commercial agencies and their producers.
              </p>
            </Reveal>
          </div>
          <RevealGroup className="grid grid-cols-1 gap-px bg-line md:col-span-6 md:col-start-7">
            {items.map((it) => (
              <RevealItem key={it.t} className="bg-paper p-8">
                <it.icon className="h-6 w-6 text-navy" strokeWidth={1.5} />
                <h3 className="mt-5 font-display text-xl font-medium tracking-tight text-ink">{it.t}</h3>
                <p className="mt-2 leading-relaxed text-subtext">{it.d}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="bg-ink py-24 text-paper md:py-32" data-testid="manifesto">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end">
          <Reveal className="md:col-span-8">
            <SectionLabel dark>The Differentiators</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Systems over personalities.
            </h2>
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.1}>
            <p className="leading-relaxed text-invtext">
              Six principles that make our pipeline development predictable, professional, and
              built to compound over time.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 border-t border-inkline">
          {DIFFERENTIATORS.map((d, i) => (
            <Reveal key={d.n} delay={i * 0.04}>
              <div className="group grid grid-cols-1 gap-4 border-b border-inkline py-8 transition-colors hover:bg-white/[0.03] md:grid-cols-12 md:gap-8 md:py-10">
                <div className="font-mono text-sm text-clay md:col-span-1">{d.n}</div>
                <h3 className="font-display text-2xl font-medium tracking-tight md:col-span-4 md:text-3xl">
                  {d.title}
                </h3>
                <p className="leading-relaxed text-invtext md:col-span-6 md:col-start-7">{d.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessPreview() {
  return (
    <section className="border-b border-line bg-paper py-24 md:py-32" data-testid="process-preview">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Reveal>
              <SectionLabel num="→">The System</SectionLabel>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display text-3xl font-medium leading-[1.08] tracking-tight text-ink sm:text-4xl">
                A disciplined process, repeated every cycle.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/how-it-works"
                className="group mt-8 inline-flex items-center gap-2 border-b border-ink pb-1 font-mono text-[12px] uppercase tracking-[0.12em] text-ink"
              >
                See the full process
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <div className="md:col-span-7 md:col-start-6">
            <div className="border-l border-line pl-8">
              {PROCESS.slice(0, 4).map((p, i) => (
                <Reveal key={p.n} delay={i * 0.06}>
                  <div className="relative pb-10 last:pb-0">
                    <span className="absolute -left-[2.6rem] top-1 flex h-4 w-4 items-center justify-center rounded-full border border-navy bg-paper">
                      <span className="h-1.5 w-1.5 rounded-full bg-navy" />
                    </span>
                    <div className="flex items-baseline gap-4">
                      <span className="font-mono text-xs text-clay">{p.n}</span>
                      <h3 className="font-display text-xl font-medium tracking-tight text-ink">{p.title}</h3>
                    </div>
                    <p className="mt-2 max-w-lg leading-relaxed text-subtext">{p.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="bg-paper py-24 md:py-32" data-testid="services-preview">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <Reveal>
          <SectionLabel num="↳">Pipeline Development Services</SectionLabel>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end">
          <Reveal className="md:col-span-8">
            <h2 className="font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              What we build for your agency.
            </h2>
          </Reveal>
          <Reveal className="md:col-span-4 md:flex md:justify-end" delay={0.1}>
            <Link
              to="/services"
              className="group inline-flex items-center gap-2 border-b border-ink pb-1 font-mono text-[12px] uppercase tracking-[0.12em] text-ink"
            >
              All services
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <RevealGroup className="mt-14 grid grid-cols-1 gap-px bg-line sm:grid-cols-2">
          {SERVICES.map((s) => (
            <RevealItem key={s.title} className="group bg-paper p-8 transition-colors hover:bg-white md:p-10">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay">{s.tag}</span>
              <h3 className="mt-4 font-display text-2xl font-medium tracking-tight text-ink">{s.title}</h3>
              <p className="mt-3 leading-relaxed text-subtext">{s.body}</p>
              <ul className="mt-6 space-y-2">
                {s.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-3 text-sm text-ink">
                    <span className="h-px w-4 bg-clay" />
                    {pt}
                  </li>
                ))}
              </ul>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
}

export default function Home() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Commercial Client Acquisition, LLC",
    alternateName: "CCA",
    description:
      "Commercial Insurance Pipeline Development for independent commercial insurance agencies — disciplined systems, standardized processes, and quality assurance.",
    areaServed: "US",
    serviceType: "Commercial Insurance Pipeline Development",
    email: COMPANY.email,
    telephone: COMPANY.phone,
    knowsAbout: [
      "Commercial Insurance Pipeline Development",
      "Commercial prospecting",
      "Insurance sales pipeline",
      "Agency growth",
      "Producer prospecting",
    ],
  };
  return (
    <div data-testid="home-page">
      <Seo
        title=""
        description="Build a predictable commercial insurance pipeline without an internal prospecting department. CCA delivers disciplined pipeline development through proven systems and quality assurance."
        path="/"
        jsonLd={jsonLd}
      />
      <Hero />
      <Marquee />
      <CategoryStatement />
      <Manifesto />
      <ProcessPreview />
      <ServicesPreview />
      <section className="bg-ink text-paper" data-testid="home-contact-cta">
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-28 lg:px-16">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:items-end">
            <Reveal className="md:col-span-8">
              <h2 className="font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
                Ready to build a predictable pipeline?
              </h2>
              <p className="mt-6 max-w-xl leading-relaxed text-invtext">
                Tell us about your agency. We'll respond promptly with next steps.
              </p>
            </Reveal>
            <Reveal className="md:col-span-4 md:flex md:justify-end" delay={0.1}>
              <Link
                to="/contact"
                data-testid="home-contact-btn"
                className="group inline-flex items-center gap-2 border border-paper bg-paper px-7 py-4 font-mono text-[12px] uppercase tracking-[0.12em] text-ink transition-colors hover:bg-transparent hover:text-paper"
              >
                Contact Us
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
